import {
  Avatar,
  Box,
  Button,
  Card,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import LOGO from '../../assets/images/logo_v3.svg';
import { numberFormat } from '../../utils/nunberFormat';

import { EN_ACCEPT_LANGUAGE } from '../../constants/acceptLanguageApi';
import { concatenateStrings } from '../../utils/concatenateStrings';
import { convertDateStringToNormalDateTime } from '../../utils/convertDateFormat';
import { OrderStatusEnum } from '../../enums/orderStatus';
import { OrderModel } from '../../models/order';
import { calculateOrderProductUnitByCarts } from '../../utils/calculateOrderProductUnitByCarts';
import { CartsModel } from '../../models/carts';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { SECONDARY_COLOR, TEXT_TITLE_COLOR } from '../../constants/colors';
import { viewGoogleMapLocation } from '../../utils/viewGoogleMapLocation';
import useOrderDetailController from './controller/orderDetailController';
import { IMAGE_RIDER_PROFILE } from '../../lib/endpoints/profileImage';
import ConfirmDialog from '../../components/ConfirmDialog';
import ConfirmCancelOrderDialog from '../../components/declineDialog';
import CardRiderProfileComponent from './components/CardRiderProfile';
import PromotionText from '../../components/PromotionText';
import { PromotionEnumType } from '../../enums/promotionEnum';
import { findOrderCreateTime } from '../../utils/findOrderCreareTime';
import OrderStatus from '../../components/OrderStatus';
import { PromotionTypeEnumPromotion } from '../../enums/discountEnum';

interface Props {
  onClose: VoidFunction;
  selectedData: OrderModel;
  onRefetchData: () => Promise<void>;
  orderStatus: OrderStatusEnum;
}

const OrderDetail = ({ onClose, selectedData, onRefetchData, orderStatus }: Props) => {
  const { i18n, t } = useTranslation('global');
  console.log('order detail', selectedData);
  const currentDate = new Date();
  // const controller = useOrderController();
  const controller = useOrderDetailController({
    selectedData,
    onClose,
    onRefetchData,
    orderStatus,
  });

  const nearByRider = () => {
    if (orderStatus !== OrderStatusEnum.To_Ship) return null;

    const renderLoading = () => <LinearProgress />;

    const renderNoRiders = () => (
      <Typography sx={{ color: 'red' }}>{t('order_page.No Rider Nearby')}</Typography>
    );

    const renderRiderDetails = () => (
      <Box>
        <Box sx={{ display: 'flex', gap: 5 }}>
          <Typography color={TEXT_TITLE_COLOR} fontSize={25} fontWeight="bold">
            {t('order_page.Order ID')} : {selectedData?.orderNo}
          </Typography>
          <Typography fontSize={25} fontWeight="bold" color={TEXT_TITLE_COLOR}>
            {t('order_page.Order number')} : {selectedData?.orderNumber}
          </Typography>
        </Box>

        <Box sx={{ mt: 1 }}>
          <Typography color={TEXT_TITLE_COLOR} fontSize={20} fontWeight="bold" mb={2}>
            {t('order_page.Nearby Rider')}
          </Typography>

          <Grid container spacing={2}>
            {controller.rowRiderUser?.map((data) => {
              const { username, profile, id }: any = data;
              return (
                <Grid item xs={6} md={2}>
                  <CardRiderProfileComponent
                    profileUrl={profile?.image?.url}
                    username={username}
                    onConfirm={() => controller.handleOpenConfirmDialog(id)}
                  />
                </Grid>
              );
            })}
          </Grid>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
          <Button
            color="secondary"
            onClick={controller.handleShowMoreRider}
            sx={{ textTransform: 'none' }}
          >
            Show more
          </Button>
        </Box>
      </Box>
    );

    return (
      <>
        {controller.loadingRiderNearby ? (
          renderLoading()
        ) : (
          <Box>
            {controller.riderData.length === 0 && !selectedData?.rider
              ? renderNoRiders()
              : renderRiderDetails()}
          </Box>
        )}
      </>
    );
  };

  return (
    <Box>
      <IconButton onClick={onClose} sx={{ mb: 1 }}>
        <KeyboardBackspaceIcon />
      </IconButton>
      {nearByRider()}
      <Typography
        variant="h5"
        color="#737373"
        fontWeight="bold"
        sx={{
          mb: 1,
          mt: 1,
        }}
      >
        {t('order_page.Order detail')}
      </Typography>
      <Button
        onClick={() =>
          viewGoogleMapLocation(
            selectedData?.merchant?.location.coordinates[1],
            selectedData?.merchant?.location.coordinates[0],
            parseFloat(selectedData?.address?.latitude),
            parseFloat(selectedData?.address?.longitude)
          )
        }
        color="secondary"
        variant="contained"
        sx={{ color: 'white', textTransform: 'none', mb: 2, mt: 1 }}
      >
        {t('order_page.View Google Map Location')}
      </Button>
      {orderStatus !== OrderStatusEnum.Failed &&
        orderStatus !== OrderStatusEnum.Return &&
        orderStatus !== OrderStatusEnum.Delivered && (
          <>
            <Button
              variant="outlined"
              color="error"
              onClick={controller.handleOpenDeclineConfirmDialog}
              sx={{ textTransform: 'none', width: '100px', color: '#6A6A6A', mb: 2, mt: 1, ml: 1 }}
            >
              {t('Decline')}
            </Button>
          </>
        )}
      <Card
        sx={{
          boxShadow:
            '0px 0px 2px rgba(145, 158, 171, 0.2), 0px 12px 24px -4px rgba(145, 158, 171, 0.12);',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <img src={LOGO} alt="" style={{ width: 104, height: 32 }} />
          <Typography fontSize={32}>
            {t('order_page.Order number')} : {selectedData?.orderNumber}
          </Typography>
          <Typography fontSize={32} fontWeight="bold">
            {t('order_page.Order ID')} : {selectedData?.orderNo}
          </Typography>
        </Box>

        <Typography>
          {t('order_page.Status')} : <OrderStatus code={selectedData?.status?.code} />
        </Typography>
        <Typography>
          {t('order_page.Payment method')} :{' '}
          <span
            style={{
              lineHeight: 3,
              backgroundColor: '#FFF2D6',
              borderRadius: '6px',
              padding: '2px 8px',
              color: '#1B806A',
              display: selectedData?.paymentMethod ? 'inline' : 'none',
            }}
          >
            {selectedData?.paymentMethod}
          </span>
        </Typography>
        <Typography>
          {t('order_page.Shipment method')} :{' '}
          <span
            style={{
              lineHeight: 3,
              backgroundColor: SECONDARY_COLOR,
              borderRadius: '6px',
              padding: '2px 8px',
              color: 'white',
              display: selectedData?.orderMethod ? 'inline' : 'none',
            }}
          >
            {selectedData?.orderMethod}
          </span>
        </Typography>
        <Typography>
          {t('order_page.Distance')}:{' '}
          <span style={{ fontWeight: 'bold' }}>
            {selectedData?.distance} {t('order_page.Km')}
          </span>
        </Typography>
        <Typography>
          {t('order_page.Delivery time')}:{' '}
          <span style={{ fontWeight: 'bold' }}>
            {t('Merchant')} : latitude: {selectedData?.merchant?.location.coordinates[1]} longitude:{' '}
            {selectedData?.merchant?.location.coordinates[0]}{' '}
            <span style={{ color: 'red' }}>ລູກຄ້າ</span>: latitude:{' '}
            {selectedData?.address?.latitude}, longitude: {selectedData?.address?.longitude}
          </span>
        </Typography>
        <Divider />
        <Box mt={2}>
          <Typography fontWeight="bold">{t('order_page.Detail User Address')}</Typography>
          <Typography>
            {t('order_page.Province')} : {selectedData?.address?.province}
          </Typography>
          <Typography>
            {t('order_page.District')} : {selectedData?.address?.district}
          </Typography>
          <Typography>
            {t('order_page.Village')} : {selectedData?.address?.village}
          </Typography>
          <Typography>
            {t('order_page.Detail')} : {selectedData?.address?.detail}
          </Typography>
        </Box>
        <Divider />
        <Box mt={5}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography color="#919EAB" fontWeight="bold">
                {t('order_page.From')}
              </Typography>
              <Box mt={2}>
                <Typography>
                  {i18n.language === EN_ACCEPT_LANGUAGE
                    ? selectedData?.merchant?.nameEn
                    : selectedData?.merchant?.nameLa}
                </Typography>
                <Typography>
                  {selectedData?.merchant?.village && selectedData?.merchant?.village}{' '}
                  {selectedData?.merchant?.district && ', ' + selectedData?.merchant?.district}{' '}
                  {selectedData?.merchant?.provice && ', ' + selectedData?.merchant?.provice}
                </Typography>
                <Typography>
                  {t('order_page.Phone')}: {selectedData?.merchant?.phone}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography color="#919EAB" fontWeight="bold">
                {t('order_page.Customer')}
              </Typography>
              <Box mt={2}>
                <Typography>{selectedData?.customer?.profile?.fullName}</Typography>
                <Typography>{selectedData?.customer?.profile?.address}</Typography>
                <Typography>
                  {t('order_page.Phone')}: {selectedData?.customer?.phone}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Box mt={5}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography color="#919EAB" fontWeight="bold">
                {t('order_page.Order Date')}
              </Typography>
              <Box mt={2}>
                <Typography>{findOrderCreateTime(selectedData)}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography color="#919EAB" fontWeight="bold">
                {t('order_page.Delivery time')}
              </Typography>
              <Typography>
                {selectedData?.deliveryTime &&
                  convertDateStringToNormalDateTime(selectedData?.deliveryTime)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
        <Box mt={10}>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow hover tabIndex={-1} role="checkbox">
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    #
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    {t('order_page.Description')}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    {t('order_page.Barcode')}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    {t('order_page.Qty')}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    {t('order_page.Unit Price')}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    {t('order_page.Discount')}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    {t('order_page.Promotion')}
                  </TableCell>
                  <TableCell align="left" sx={{ color: '#637381' }}>
                    {t('order_page.Total')}
                  </TableCell>
                </TableRow>
                {selectedData?.carts?.map((item: CartsModel, index: number) => {
                  return (
                    <TableRow key={index} hover tabIndex={-1} role="checkbox">
                      <TableCell align="left">{numberFormat(index + 1)}</TableCell>
                      <TableCell align="left">
                        {i18n.language === EN_ACCEPT_LANGUAGE
                          ? item?.product?.price?.masterProduct?.productNameEn
                          : item?.product?.price?.masterProduct?.productNameLa}
                      </TableCell>
                      <TableCell align="left">
                        {concatenateStrings(item?.product?.price?.masterProduct?.barcodes)}
                      </TableCell>
                      <TableCell align="left">{numberFormat(item?.quantity)}</TableCell>
                      <TableCell align="left">
                        {numberFormat(item?.productPrice)} {t('order_page.kip')}
                      </TableCell>
                      <TableCell align="left">
                        {numberFormat(item?.discount)} {t('order_page.kip')}
                      </TableCell>
                      <TableCell align="left">
                        {/* <PromotionText
                          promotionType={
                            item?.product?.promotion?.promotionType as PromotionEnumType
                          }
                        /> */}
                        {item?.product?.promotion &&
                          new Date(item.product.promotion.startDate) <= currentDate &&
                          new Date(item.product.promotion.endDate) >= currentDate && (
                            <PromotionText
                              promotionType={
                                item?.product?.promotion?.promotionType as PromotionEnumType
                              }
                            />
                          )}
                      </TableCell>
                      <TableCell align="left">
                        {numberFormat(item?.total)} {t('order_page.kip')}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {selectedData?.carts?.some((item) => item?.getProduct) &&
              selectedData?.carts?.length > 0 && (
                <>
                  <Typography variant="h6" sx={{ color: 'red', mt: 2 }}>
                    {' '}
                    {t('promotion_page.Promotion Product')}
                  </Typography>
                  <Table>
                    <TableBody>
                      <TableRow hover tabIndex={-1} role="checkbox">
                        <TableCell align="left" sx={{ color: '#637381' }}>
                          #
                        </TableCell>
                        <TableCell align="left" sx={{ color: '#637381' }}>
                          {t('order_page.Description')}
                        </TableCell>
                        <TableCell align="left" sx={{ color: '#637381' }}>
                          {t('order_page.Barcode')}
                        </TableCell>
                        <TableCell align="left" sx={{ color: '#637381' }}>
                          {t('order_page.Qty')}
                        </TableCell>
                        <TableCell align="left" sx={{ color: '#637381' }}>
                          {t('order_page.Unit Price')}
                        </TableCell>
                        <TableCell align="left" sx={{ color: '#637381' }}>
                          {t('order_page.Promotion')}
                        </TableCell>
                        <TableCell align="left" sx={{ color: '#637381' }}>
                          {t('order_page.Total')}
                        </TableCell>
                      </TableRow>
                      {selectedData?.carts?.map((item: CartsModel, index: number) => {
                        return (
                          <TableRow key={index} hover tabIndex={-1} role="checkbox">
                            <TableCell align="left">{numberFormat(index + 1)}</TableCell>
                            <TableCell align="left">
                              {i18n.language === EN_ACCEPT_LANGUAGE
                                ? item?.getProduct?.product?.masterProduct?.productNameEn
                                : item?.getProduct?.product?.masterProduct?.productNameLa}
                            </TableCell>
                            <TableCell align="left">
                              {concatenateStrings(
                                item?.getProduct?.product?.masterProduct?.barcodes
                              )}
                            </TableCell>
                            <TableCell align="left">
                              {numberFormat(selectedData?.carts[0]?.getProduct?.quantity)}
                            </TableCell>
                            <TableCell align="left">
                              {numberFormat(item?.getProduct?.product?.price)} {t('order_page.kip')}
                            </TableCell>
                            <TableCell align="left">
                              <PromotionText
                                promotionType={
                                  item?.product?.promotion?.promotionType as PromotionEnumType
                                }
                              />
                            </TableCell>
                            <TableCell align="left">
                              {numberFormat(
                                item?.getProduct?.product?.price *
                                item?.getProduct?.product?.masterProduct?.quantity
                              )}{' '}
                              {t('order_page.kip')}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
          </TableContainer>
        </Box>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'end', gap: 14 }}>
          <Box>
            <Typography textAlign="end">{t('order_page.Total unit')}</Typography>
            <Typography textAlign="end">{t('order_page.Subtotal')}</Typography>
            <Typography textAlign="end">{t('order_page.Taxes')}</Typography>
            <Typography textAlign="end">{t('order_page.Delivery fee')}</Typography>
            <Typography textAlign="end">{t('order_page.Discount price')}</Typography>
            <Typography textAlign="end">{t('order_page.Discount promotion')}</Typography>
            <Typography textAlign="end">
              {t('order_page.Discount coupon')}{' '}
              {selectedData?.couponCode && (
                <span style={{ color: 'red' }}>
                  {t('order_page.With Coupon Code')} : {selectedData?.couponCode}
                </span>
              )}
            </Typography>
            <Typography textAlign="end">{t('order_page.Get Point In this Order')}</Typography>
            <Typography textAlign="end" fontWeight="bold">
              {t('order_page.Total')}
            </Typography>
          </Box>
          <Box>
            <Typography textAlign="end">
              {numberFormat(calculateOrderProductUnitByCarts(selectedData?.carts))}{' '}
              {t('order_page.units')}
            </Typography>
            <Typography textAlign="end">
              {numberFormat(selectedData?.totalNomalPriceNotIncludeVat)} {t('order_page.kip')}
            </Typography>
            <Typography textAlign="end">
              {numberFormat(selectedData?.totalVatPrice)} {t('order_page.kip')}
            </Typography>
            <Typography textAlign="end">
              {numberFormat(selectedData?.deliveryFee)} {t('order_page.kip')}
            </Typography>
            <Typography textAlign="end" color="red">
              - {numberFormat(selectedData?.totalDiscountPrice)} {t('order_page.kip')}
            </Typography>
            <Typography textAlign="end" color="red">
              - {numberFormat(selectedData?.totalDiscountPriceForPromotion)} {t('order_page.kip')}
            </Typography>
            <Typography textAlign="end" color="red">
              - {numberFormat(selectedData?.totalDiscountPriceForCoupon)} {t('order_page.kip')}
            </Typography>
            <Typography textAlign="end" fontWeight="bold">
              {numberFormat(selectedData?.getPoint)} {t('order_page.Point')}
            </Typography>
            <Typography textAlign="end" fontWeight="bold">
              {numberFormat(selectedData?.totoalWithDeliveryFee)} {t('order_page.kip')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ width: '100%', mt: 2, mb: 2 }}>
          <Box sx={{ backgroundColor: '#C8C8C8;', height: '1px' }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <Box>
              <Typography>
                {t('order_page.Driver Name')} :
                <span style={{ fontWeight: 'bold', fontSize: '20px', marginLeft: '10px' }}>
                  {' '}
                  {selectedData?.rider?.username}
                </span>
              </Typography>
              <Typography>
                {t('order_page.Driver contact')} :
                <span style={{ fontWeight: 'bold', fontSize: '20px', marginLeft: '10px' }}>
                  {selectedData?.rider?.profile?.phoneNumber}{' '}
                </span>{' '}
              </Typography>
            </Box>
            <Avatar
              alt="Rider"
              sx={{
                width: '80px',
                height: '80px',
              }}
              src={IMAGE_RIDER_PROFILE + selectedData?.rider?.profile?.image?.url}
            />
          </Box>
          <Box>
            <Typography>{t('order_page.Contact admin for support')} : admin@gmail.com</Typography>
            <Typography>020 554562236</Typography>
          </Box>
        </Box>
      </Card>
      <ConfirmDialog
        open={controller.openConfirmDialog}
        title="ທ່ານຕ້ອງການມອບອໍເດີ້ນີ້ໃຫ້ຄົນຂັບບໍ ?"
        loading={controller.loadingConfirmDialog}
        onCancel={controller.handleCloseConfirmDialog}
        onConfirm={() => controller.handleAssignJobToRider()}
      />
      <ConfirmCancelOrderDialog
        open={controller.openConfirmDeclineDialog}
        title={t('order_page.Do you want to decline this order?')}
        loading={controller.loadingConfirmDeclineDialog}
        onCancel={controller.handleCloseDeclineConfirmDialog}
        onConfirm={controller.handleDeclineConfirmDialog}
      />
    </Box>
  );
};
export default OrderDetail;
